import React from 'react';
import { Link } from 'react-router-dom';
import BlueCallToAction from '../../components/common/BlueCallToAction';

/**
 * Automation Solution Page
 * End-user focused view of Videxa's automation capabilities
 */
const AutomationSolutionPage = () => {
  const automationAreas = [
    {
      title: "Prior Authorization",
      description: "Automate the time-consuming prior authorization process to reduce physician burden and speed approvals.",
      benefits: [
        "Auto-submission to payers",
        "Status tracking & alerts",
        "Appeal generation",
        "Documentation assembly"
      ],
      stat: "80%",
      statLabel: "Reduction in PA turnaround time"
    },
    {
      title: "Eligibility Verification",
      description: "Real-time verification of patient coverage, benefits, and financial responsibility at every encounter.",
      benefits: [
        "Real-time payer connectivity",
        "Automated benefit checks",
        "Coverage gap alerts",
        "Patient responsibility estimates"
      ],
      stat: "99%",
      statLabel: "Verification accuracy"
    },
    {
      title: "Claims Processing",
      description: "From charge capture to payment posting, automate every step of the claims lifecycle.",
      benefits: [
        "Auto-charge posting",
        "Claim scrubbing & edits",
        "Electronic submission",
        "ERA/EOB processing"
      ],
      stat: "94%",
      statLabel: "Clean claim rate"
    },
    {
      title: "Denial Management",
      description: "Intelligent automation of denial prevention, tracking, and appeals to maximize revenue recovery.",
      benefits: [
        "Predictive denial alerts",
        "Root cause analysis",
        "Auto-appeal generation",
        "Deadline tracking"
      ],
      stat: "65%",
      statLabel: "Fewer denials"
    }
  ];

  const workflowAutomations = [
    {
      title: "Document Processing",
      description: "AI-powered extraction and routing of clinical documents, faxes, and correspondence.",
      icon: "📄"
    },
    {
      title: "Task Assignment",
      description: "Intelligent work queue management that routes tasks to the right staff based on skills and workload.",
      icon: "📋"
    },
    {
      title: "Patient Communication",
      description: "Automated appointment reminders, billing notifications, and payment requests.",
      icon: "📱"
    },
    {
      title: "Reporting",
      description: "Scheduled reports and real-time dashboards delivered automatically to stakeholders.",
      icon: "📊"
    },
    {
      title: "Compliance Monitoring",
      description: "Continuous monitoring of regulatory requirements with automated alerts for issues.",
      icon: "✓"
    },
    {
      title: "Data Integration",
      description: "Automated data synchronization between your healthcare systems and Videxa.",
      icon: "🔗"
    }
  ];

  const aiCapabilities = [
    {
      title: "Natural Language Processing",
      description: "Understand and extract information from clinical notes, correspondence, and unstructured documents.",
      applications: ["CDI query generation", "Documentation analysis", "Coding suggestions"]
    },
    {
      title: "Predictive Analytics",
      description: "Machine learning models that anticipate issues before they become problems.",
      applications: ["Denial prediction", "Cash flow forecasting", "Risk stratification"]
    },
    {
      title: "Intelligent Automation",
      description: "RPA combined with AI for complex decision-making in routine workflows.",
      applications: ["Claim editing", "Authorization decisions", "Payment posting"]
    }
  ];

  const implementationSteps = [
    { step: "1", title: "Discovery", description: "We analyze your current workflows and identify automation opportunities" },
    { step: "2", title: "Configuration", description: "Customize automation rules to match your processes and preferences" },
    { step: "3", title: "Integration", description: "Connect Videxa with your EHR, PM, and other systems" },
    { step: "4", title: "Training", description: "Your team learns to use and optimize the automated workflows" },
    { step: "5", title: "Optimization", description: "Continuous improvement based on performance data and feedback" }
  ];

  return (
    <div className="automation-solution-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              Intelligent <span style={{ color: '#FFC000' }}>Automation</span>
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-gray-700">
              Automate routine tasks. Empower your team. Accelerate revenue.
            </p>
            <p className="text-lg mb-8 text-gray-600">
              Videxa's AI-powered automation eliminates manual processes, reduces errors, and frees your staff to focus on high-value work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/cdi-trial"
                style={{
                  backgroundColor: '#0E3056',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600'
                }}
                className="hover:opacity-90 transition-opacity"
              >
                Start Free Trial
              </Link>
              <Link
                to="/contact"
                style={{
                  border: '2px solid #0E3056',
                  color: '#0E3056',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600'
                }}
                className="hover:bg-[#0E3056] hover:text-white transition-colors"
              >
                Schedule Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-16 bg-[#0E3056] text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">
            The Power of Automation
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFC000] mb-2">40%</div>
              <p className="text-gray-200">Staff Time Saved</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFC000] mb-2">90%</div>
              <p className="text-gray-200">Reduction in Manual Errors</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFC000] mb-2">3x</div>
              <p className="text-gray-200">Faster Processing</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFC000] mb-2">25%</div>
              <p className="text-gray-200">Revenue Increase</p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Automation Areas */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Key Automation Areas
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            Targeted automation for your most time-consuming processes
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {automationAreas.map((area, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg"
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {area.title}
                  </h3>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-[#0E3056] dark:text-[#FFC000]">
                      {area.stat}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {area.statLabel}
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {area.description}
                </p>
                <ul className="grid grid-cols-2 gap-2">
                  {area.benefits.map((benefit, idx) => (
                    <li key={idx} className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Automations */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Workflow Automations
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            Streamline operations across your organization
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {workflowAutomations.map((workflow, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="text-4xl mb-4">{workflow.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  {workflow.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {workflow.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Capabilities */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            AI-Powered Intelligence
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            Beyond simple automation—true artificial intelligence
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {aiCapabilities.map((capability, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg"
              >
                <h3 className="text-xl font-semibold text-[#0E3056] dark:text-[#FFC000] mb-3">
                  {capability.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {capability.description}
                </p>
                <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Applications:
                  </p>
                  <ul className="space-y-1">
                    {capability.applications.map((app, idx) => (
                      <li key={idx} className="text-sm text-gray-700 dark:text-gray-300">
                        • {app}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-gray-900 dark:text-white text-center">
            Simple Implementation
          </h2>

          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start gap-4">
              {implementationSteps.map((item, index) => (
                <div key={index} className="flex-1 text-center relative">
                  <div className="bg-[#0E3056] dark:bg-[#FFC000] text-white dark:text-gray-900 w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{item.description}</p>
                  {index < implementationSteps.length - 1 && (
                    <div className="hidden md:block absolute top-6 left-[60%] w-[80%] h-0.5 bg-gray-200 dark:bg-gray-700"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <BlueCallToAction
        heading="Ready to automate your revenue cycle?"
        description="Discover how Videxa's intelligent automation can transform your operations."
        primaryButtonText="Start Free Trial"
        primaryButtonLink="/cdi-trial"
        secondaryButtonText="Schedule Demo"
        secondaryButtonLink="/contact"
      />
    </div>
  );
};

export default AutomationSolutionPage;
